/**
 * ReconnectForm Component
 * 
 * A reusable session reconnection form for when a user's session has
 * expired but they want to continue working without a full re-login.
 * 
 * @example
 * ```typescript
 * const reconnectForm = new ReconnectForm(container, {
 *   email: 'user@example.com',
 *   userName: 'John Doe',
 *   onSuccess: (user) => {
 *     console.log('Reconnected:', user.email);
 *   },
 *   onSwitchUser: () => showLoginForm()
 * });
 * await reconnectForm.initialize();
 * ```
 */

import { IComponent } from '../IComponent';
import { ReconnectFormOptions, AuthUser } from './AuthTypes';
import { ToasterService } from '@/services/ToasterService';
import { authService } from '@/services/Authentication';
import { oemConfig } from '@/services/OEMConfig';
import { Logger } from '@/services/Logger';

const logger = Logger.scope('ReconnectForm');

export class ReconnectForm implements IComponent {
  private container: HTMLElement;
  private options: ReconnectFormOptions;

  constructor(container: HTMLElement, options: ReconnectFormOptions) {
    this.container = container;
    this.options = options;
  }

  async initialize(): Promise<void> {
    ToasterService.init();
    this.render();
    this.bindEvents();
    logger.debug('ReconnectForm initialized');
  }

  destroy(): void {
    this.container.innerHTML = '';
    logger.debug('ReconnectForm destroyed');
  }

  /**
   * Render the reconnect form HTML
   */
  private render(): void {
    const oem = {
      logo: oemConfig.getSplashImage(),
      caption: oemConfig.getBrandName()
    };

    this.container.innerHTML = `
      <div class="reconnect-form-container w-full max-w-md">
        <div class="text-center mb-8">
          <img src="${oem.logo}" alt="${oem.caption}" class="h-16 mx-auto mb-4">
          <p class="text-base-content/70 mt-2">Your session has expired. Please re-authenticate.</p>
        </div>

        <div class="alert alert-warning mb-6">
          <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span>For security, your session has timed out due to inactivity.</span>
        </div>

        <form id="reconnect-form-component" class="space-y-4">
          <!-- Email (readonly) -->
          <div class="form-control">
            <label class="label">
              <span class="label-text">Email</span>
            </label>
            <input 
              type="email" 
              id="reconnect-email-input"
              name="email" 
              autocomplete="email"
              value="${this.options.email}" 
              class="input input-bordered w-full" 
              readonly 
            />
          </div>

          <!-- Password -->
          <div class="form-control">
            <label class="label">
              <span class="label-text">Password</span>
            </label>
            <input 
              type="password" 
              id="reconnect-password-input"
              name="password" 
              autocomplete="current-password"
              placeholder="Enter your password" 
              class="input input-bordered w-full" 
              required 
              autofocus
            />
          </div>

          <!-- Submit Button -->
          <button type="submit" id="reconnect-submit-btn" class="btn btn-info w-full">
            <i class="fas fa-plug"></i>
            Reconnect
          </button>

          ${this.options.onSwitchUser ? `
            <!-- Links -->
            <div class="text-center text-sm mt-4">
              <p>
                <a role="button" id="reconnect-switch-user-link" class="link link-primary cursor-pointer">Switch user</a>
              </p>
            </div>
          ` : ''}
        </form>
      </div>
    `;
  }

  /**
   * Bind event listeners
   */
  private bindEvents(): void {
    const form = this.container.querySelector('#reconnect-form-component') as HTMLFormElement;
    const switchUserLink = this.container.querySelector('#reconnect-switch-user-link');

    // Form submission
    form?.addEventListener('submit', (e) => this.handleSubmit(e));

    // Switch user link
    switchUserLink?.addEventListener('click', (e) => {
      e.preventDefault();
      this.options.onSwitchUser?.();
    });
  }

  /**
   * Handle form submission
   */
  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault();

    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    const password = formData.get('password') as string;

    if (!password) {
      ToasterService.warning('Please enter your password');
      return;
    }

    const submitBtn = this.container.querySelector('#reconnect-submit-btn') as HTMLButtonElement;
    const originalText = submitBtn.innerHTML;
    submitBtn.disabled = true;
    submitBtn.innerHTML = '<span class="loading loading-spinner"></span> Reconnecting...';

    try {
      const result = await authService.login({ email: this.options.email, password });

      if (result.success) {
        const user: AuthUser = {
          id: result.user?.id || 'user-1',
          email: this.options.email,
          name: this.options.userName || result.user?.name || this.options.email.split('@')[0],
          avatar: result.user?.avatar
        };

        ToasterService.success('Session reconnected successfully');
        this.options.onSuccess(user);
      } else {
        ToasterService.error(result.message || 'Failed to reconnect. Please try again.');
        submitBtn.disabled = false;
        submitBtn.innerHTML = originalText;
      }
    } catch (error) {
      logger.error('Failed to reconnect:', error);
      ToasterService.error('Failed to reconnect. Please try again.');
      this.options.onError?.(error as Error);
      submitBtn.disabled = false;
      submitBtn.innerHTML = originalText;
    }
  }
}
